import React, { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { DataGrid } from '@mui/x-data-grid';
import { Button, Card, CardContent, CircularProgress, Grid, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { show as showIoCredential } from 'api/ioCredentialsApi';
import moment from 'moment';
import withShowAlert from 'components/withShowAlert';
import { index, redo, show as showMessage } from 'api/messagesApi';
import { Done, Error, FileDownloadOff, Pending, PriorityHigh, Replay, SdCardAlert, SmsFailed, TimerOff, Search } from '@mui/icons-material';
import { canRedo, getStatusLabelForMessage } from 'util/messages';
import MercadappDialog from 'components/MercadappDialog';
import MessageFilter from 'components/MessageFilter';
import { calculateTimeDiff } from 'util/helpers';
import MessageDetails from 'components/MessageDetails';
import ManageTopics from 'components/ManageTopics';
import SettingsIcon from '@mui/icons-material/Settings';

const StatusHinter = ({ statusLabel, statusHint, statusIcon }) => {
  return (
    <Grid item xs={3} mt={2} p={1}>
      <Grid container>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {statusIcon}<strong style={{ fontSize: 14 }}>{statusLabel}</strong>
        </Box>
      </Grid>
      <Grid container mt={.5}>
        <Typography sx={{ marginLeft: 4, fontSize: 12 }} variant='caption'>{statusHint}</Typography>
      </Grid>
    </Grid>)
}

function Show({ showAlert }) {
  let { ioCredentialId } = useParams();
  const columns = [
    {
      field: 'topic',
      headerName: 'Tópico',
      width: 150,
    },
    {
      field: 'entity_id',
      headerName: 'ID da Entidade',
      width: 150,
      valueGetter: (params) => params.row.entity_id
    },
    {
      field: 'created_at',
      headerName: 'Criado em',
      width: 200,
      valueGetter: (params) => moment(params.row.created_at).format("DD/MM/YYYY HH:mm:ss"),
    },
    {
      field: 'process_time',
      headerName: 'Tempo de Processamento',
      width: 200,
      valueGetter: (params) => calculateTimeDiff(params.row.process_start_at, params.row.process_end_at, "HH:mm:ss:SSSS"),
    },
    {
      headerName: 'Status',
      width: 200,
      field: 'status',
      renderCell: (params) => {
        const { label, icon } = getStatusLabelForMessage(params.row)
        return (
          <Box sx={{ display: 'flex' }}>
            {icon}{label}
          </Box>
        )
      },
    },
    {
      headerName: 'Tipo do ACK',
      width: 150,
      field: 'ack_kind',
      valueGetter: (params) => params.row.acknowledgement?.kind
    },
    {
      headerName: 'Ações',
      width: 100,
      renderCell: (params) => {
        const redoAllowed = canRedo(params.row)
        return (
          <>
          <Tooltip title="Ver detalhes">
            <IconButton
              onClick={async () => {
                try {
                  const response = await showMessage(params.row.id)
                  setSelectedMessage(response.data)
                } catch (e) {
                  setSelectedMessage(params.row)
                }
              }}
            >
              <Search />
            </IconButton>
          </Tooltip>
          <Tooltip title={redoAllowed ? 'Reenviar mensagem' : 'Mensagem já enviada ou aguardando processamento'}>
            <IconButton
              onClick={() => setRedoModalInfo({ id: params.row.id, open: true })}
              disabled={!redoAllowed}>
              <Replay />
            </IconButton>
          </Tooltip>
          </>
        )
      }
    }
  ];
  const receivedMessagesColumns = columns.filter(column => column.headerName !== 'Tipo do ACK')

  const [ioCredential, setIoCredential] = useState(null)
  const [sentMessages, setSentMessages] = useState([])
  const [receivedMessages, setReceivedMessages] = useState([])
  const [isLoadingCredentials, setIsLoadingCredentials] = useState(false)
  const [isLoadingSentMessages, setIsLoadingSentMessages] = useState(true)
  const [isLoadingReceivedMessages, setisLoadingReceivedMessages] = useState(true)
  const [currentSentPage, setCurrentSentPage] = useState(1)
  const [sentRowCount, setSentRowCount] = useState(0)
  const [redoModalInfo, setRedoModalInfo] = useState({ id: null, loading: false, open: false })
  const [currentReceivedPage, setCurrentReceivedPage] = useState(1)
  const [receivedRowCount, setReceivedRowCount] = useState(0)
  const [filters, setFilters] = useState({})
  const [selectedMessage, setSelectedMessage] = useState(null)
  const [openTopicsModal, setOpenTopicsModal] = useState(false)

  const loadIoCredential = useCallback(async () => {
    setIsLoadingCredentials(true)
    const response = await showIoCredential(ioCredentialId)
    console.log(response.data)
    setIoCredential(response.data.io_credential)
    setFilters(response.data.message_enums)
    setIsLoadingCredentials(false)
  }, [ioCredentialId])

  const loadSentMessagesForAppId = useCallback(async () => {
    if (!ioCredential?.id) return

    setIsLoadingSentMessages(true)
    try {
      const response = await index("export", currentSentPage, ioCredential.id);
      setSentMessages(response.data.data)
      setSentRowCount(response.data.total_count)
    } catch (e) {
      showAlert('Erro ao carregar mensagens enviadas.', 'error')
    } finally {
      setIsLoadingSentMessages(false)
    }
  }, [currentSentPage, ioCredential?.id, showAlert])
  
  const loadReceivedMessagesForAppId = useCallback(async () => {
    if (!ioCredential?.id) return

    setisLoadingReceivedMessages(true)
    try {
      const response = await index("import", currentReceivedPage, ioCredential.id);
      setReceivedMessages(response.data.data)
      setReceivedRowCount(response.data.total_count)
    } catch (e) {
      showAlert('Erro ao carregar mensagens recebidas.', 'error')
    } finally {
      setisLoadingReceivedMessages(false)
    }
  }, [currentReceivedPage, ioCredential?.id, showAlert])

  const redoMessage = async (messageId) => {
    try {
      const response = await redo(messageId)

      showAlert(
        response.data.message,
        'success',
      )
      loadSentMessagesForAppId()
      loadReceivedMessagesForAppId()
    } catch (error) {
      showAlert(
        error.response?.data?.message,
        'success',
      )
    }
  }

  useEffect(() => {
    loadIoCredential()
  }, [loadIoCredential])

  useEffect(() => {
    loadSentMessagesForAppId()
  }, [loadSentMessagesForAppId, currentSentPage])

  useEffect(() => {
    loadReceivedMessagesForAppId()
  }, [loadReceivedMessagesForAppId, currentReceivedPage])

  return (
    <Box sx={{ height: '82vh', width: '100%' }}>
      {selectedMessage ? <MessageDetails message={selectedMessage} onClose={() => setSelectedMessage(null)} /> : null}
      <MercadappDialog title="Tem certeza?" message="Essa ação irá reprocessar a mensagem, deixando ela como pendente."
        open={redoModalInfo.open}
        setOpen={(open) => setRedoModalInfo({ ...redoModalInfo, open: open })}
        buttons={
          [{
            text: "Cancelar", color: "primary", action: () => {
              setRedoModalInfo({ open: false, id: null })
            }
          }]
      }
        loadingButtons={[{
            text: "Continuar",
            loading: redoModalInfo.loading,
            color: "info",
            action: () => {
              redoMessage(redoModalInfo.id)
              setRedoModalInfo({ open: false, id: null })
            }
          }]} />
      {isLoadingCredentials ? (
        <Grid container mt={1} spacing={2}>
          <Grid item xs={12} sx={{ textAlign: 'center' }}>
            <CircularProgress color="primary" />
          </Grid>
        </Grid>
      ) :
      (
        <>
        <Button
          startIcon={<SettingsIcon />}
          color="primary"
          variant="contained"
          sx={{ display: 'flex', mb: 2, ml: "auto" }}
          onClick={() => setOpenTopicsModal(true)}
          >
          Gerenciar Tópicos
        </Button>
        <Card>
          <CardContent>
            <Grid container>
              <Grid xs={12}>
                <Typography variant='body'><strong>Marca: </strong>{ioCredential?.brand_name}</Typography>
              </Grid>
              {ioCredential?.market_name && <Grid xs={12}>
                <Typography variant='body'><strong>Loja: </strong>{ioCredential?.market_name}</Typography>
              </Grid>}
              <Grid xs={12}>
                <Typography variant='body'><strong>Credencial: </strong>{ioCredential?.app_id}</Typography>
              </Grid>
            </Grid>
            <Grid container>
              <Grid xs={12}>
                <Typography variant='body'><strong>Tópicos habilitados: </strong>{ioCredential?.enabled_import_topics.join(', ')}</Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        </>
      )
      }
      <Grid container mt={2} spacing={2}>
        <Grid item xs={12}>
          <Typography variant='h6' mb={1}>Legenda</Typography>
          <Card>
            <CardContent>
              <Grid container>
                <StatusHinter
                  statusLabel="Sucesso"
                  statusHint="Mensagem enviada com sucesso"
                  statusIcon={<Done color="success" sx={{ marginRight: 1, fontSize: 25 }} />} />
                <StatusHinter
                  statusLabel="Pendente"
                  statusHint="Mensagem aguardando processamento"
                  statusIcon={<Pending color="warning" sx={{ marginRight: 1, fontSize: 25 }} />} />
                <StatusHinter
                  statusLabel="Falha de acknowledgement"
                  statusHint="Mensagem de confirmação de recebimento com falha"
                  statusIcon={<SmsFailed color="error" sx={{ marginRight: 1, fontSize: 25 }} />} />
                <StatusHinter
                  statusLabel="Erro de importação"
                  statusHint="Erro ao inserir dados no Merconnect"
                  statusIcon={<Error color="error" sx={{ marginRight: 1, fontSize: 25 }} />} />
                <StatusHinter
                  statusLabel="Importação inválida"
                  statusHint="Dados não suportados pelo Merconnect"
                  statusIcon={<SdCardAlert color="error" sx={{ marginRight: 1, fontSize: 25 }} />} />
                <StatusHinter
                  statusLabel="Erro de exportação"
                  statusHint="Falha no envio de mensagem do Merconnect ao Linx I/O"
                  statusIcon={<FileDownloadOff color="error" sx={{ marginRight: 1, fontSize: 25 }} />} />
                <StatusHinter
                  statusLabel="Exportação inválida"
                  statusHint="Dados da mensagem de exportação inválida"
                  statusIcon={<PriorityHigh color="error" sx={{ marginRight: 1, fontSize: 25 }} />} />
                <StatusHinter
                  statusLabel="Obsoleto"
                  statusHint="Mensagens antigas que chegaram com atraso"
                  statusIcon={<TimerOff color="warning" sx={{ marginRight: 1, fontSize: 25 }} />} />
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Grid container mt={2} spacing={2}>
        <Grid item xs={12} mb={2}>
          <Typography variant='h6' mb={1}>Mensagens recebidas</Typography>
          <Card sx={{p: 2}}>
          <MessageFilter
            kind="import"
            filters={filters}
            setIsLoading={setisLoadingReceivedMessages}
            loadData={loadReceivedMessagesForAppId}
            onSearch={(data) => {
              setReceivedMessages(data.data)
              setReceivedRowCount(data.total_count)
            }}
          />
            <DataGrid
              rows={receivedMessages}
              columns={columns}
              loading={isLoadingReceivedMessages}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 25, page: currentReceivedPage },
                },
              }}
              rowsPerPageOptions={[25]}
              pagination
              page={currentReceivedPage}
              pageSize={5}
              rowCount={receivedRowCount}
              autoHeight={true}
              paginationMode="server"
              onPaginationModelChange={(newPaginationModel) => setCurrentReceivedPage(newPaginationModel.page)}
              disableSelectionOnClick
              hideFooterSelectedRowCount={true}
              disableColumnSelector
              disableColumnMenu
              disableColumnFilter
              sx={{
                '.MuiTablePagination-displayedRows': {
                  display: 'none'
                },
                '.MuiTablePagination-selectLabel': {
                  display: 'none'
                },
                '.MuiInputBase-root': {
                  display: 'none'
                }, '.MuiTablePagination-input': {
                  display: 'none',
                },
              }}
            />
          </Card>
          <Typography variant='h6' mt={3} mb={1}>Mensagens enviadas</Typography>
          <Card sx={{p: 2}}>
          <MessageFilter
            kind="export"
            filters={filters}
            setIsLoading={setIsLoadingSentMessages}
            loadData={loadSentMessagesForAppId}
            onSearch={(data) => {
              setSentMessages(data.data)
              setSentRowCount(data.total_count)
            }}
          />
            <DataGrid
              rows={sentMessages}
              columns={receivedMessagesColumns}
              loading={isLoadingSentMessages}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 25, page: currentSentPage },
                },
              }}
              rowsPerPageOptions={[25]}
              pagination
              page={currentSentPage}
              pageSize={5}
              rowCount={sentRowCount}
              autoHeight={true}
              paginationMode="server"
              onPaginationModelChange={(newPaginationModel) => setCurrentSentPage(newPaginationModel.page)}
              disableSelectionOnClick
              hideFooterSelectedRowCount={true}
              disableColumnSelector
              disableColumnMenu
              disableColumnFilter
              sx={{
                '.MuiTablePagination-displayedRows': {
                  display: 'none'
                },
                '.MuiTablePagination-selectLabel': {
                  display: 'none'
                },
                '.MuiInputBase-root': {
                  display: 'none'
                }, '.MuiTablePagination-input': {
                  display: 'none',
                },
              }}
            />
          </Card>
        </Grid>
      </Grid>
      <ManageTopics
        open={openTopicsModal}
        setOpen={setOpenTopicsModal}
        ioCredential={ioCredential}
        onSaved={loadIoCredential}
      />
    </Box>
  );
}

export default withShowAlert(Show)